/**
 * Which of a turnaround's three clocks speaks for it.
 *
 * A route carries a planned arrival at its injection, a live ETA once Samsara has one, and an
 * actual once the geofence fires. The board answers "when is this van getting here" with the most
 * current of the three, and it has to answer it the same way everywhere: the arrivals board sorts
 * on it, the desktop row prints it, the activity chart buckets on it, and the mobile card leads
 * with it in large type. Four copies of `actual || estimated || planned` stay correct right up
 * until somebody adds a fourth clock to one of them.
 *
 * It also says *which* kind of time it is, for the card. A table has a column header
 * and three neighbouring times to read a number against; a card has one number, large, that a
 * depot manager is about to plan a shift on -- so the kind is part of the answer, not decoration.
 */

/** Where an arrival time came from. */
export type ArrivalKind = "actual" | "eta" | "planned"

export interface TurnaroundArrival {
  at: Date
  kind: ArrivalKind
}

/** The clocks, named as `TurnaroundRoute` names them. */
export interface ArrivalInput {
  plannedArrival: Date
  estimatedArrival?: Date
  actualArrival?: Date
}

/**
 * Most current first: what happened, else what is predicted, else what was booked.
 *
 * `plannedArrival` is required rather than optional because there is always one -- the board reads
 * it off the injection stop, falling back to the route's planned end -- so this never has to
 * report that it does not know.
 */
export function arrivalOf(turnaround: ArrivalInput): TurnaroundArrival {
  if (turnaround.actualArrival) return { at: turnaround.actualArrival, kind: "actual" }
  if (turnaround.estimatedArrival) return { at: turnaround.estimatedArrival, kind: "eta" }
  return { at: turnaround.plannedArrival, kind: "planned" }
}

/** The words for each kind. "Live ETA" is the badge the desktop row already uses. */
export const ARRIVAL_LABELS: Record<ArrivalKind, string> = {
  actual: "Arrived",
  eta: "Live ETA",
  planned: "Planned",
}
